import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { cart } from '@sorare/core/src/lib/glossary';

import { FlexCard } from 'components/token/FlexCard';
import { useIsPrimaryBuyLive } from 'hooks/primaryBuy/useIsPrimaryBuyLive';
import { useCart } from 'hooks/useCart';

import { CollectionPrediction } from '../CollectionPrediction';
import type { CollectionUpsellCard_anyCard } from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  max-width: 174px;
  align-items: center;
  padding: var(--double-unit);
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-150);
`;

type Props = {
  anyCard: CollectionUpsellCard_anyCard;
};

export const CollectionUpsellCard = ({ anyCard }: Props) => {
  const primaryBuy = useIsPrimaryBuyLive(anyCard);
  const { toMonetaryAmount } = useMonetaryAmount();
  const { addCardToCart } = useCart();

  if (!primaryBuy) return null;

  return (
    <Wrapper className="text-center">
      <FlexCard card={anyCard} width={160} />
      <CollectionPrediction card={anyCard} />
      <AmountWithConversion
        monetaryAmount={toMonetaryAmount(primaryBuy.price)}
        column
      />
      <LoadingButton
        loading={addCardToCart.loading}
        size="small"
        color="quaternary"
        onClick={async () => addCardToCart.mutate(anyCard)}
      >
        <FormattedMessage {...cart.addToCart} />
      </LoadingButton>
    </Wrapper>
  );
};

CollectionUpsellCard.fragments = {
  anyCard: gql`
    fragment CollectionUpsellCard_anyCard on AnyCardInterface {
      slug
      ...FlexCard_anyCard
      ...CollectionPrediction_anyCard
      ...useIsPrimaryBuyLive_anyCard
      ...useCart_anyCard
    }
    ${FlexCard.fragments.anyCard}
    ${useIsPrimaryBuyLive.fragments.anyCard}
    ${CollectionPrediction.fragments.anyCard}
    ${useCart.fragments.anyCard}
  ` as TypedDocumentNode<CollectionUpsellCard_anyCard>,
};
